// Shared, dependency-free date formatting helpers.
//
// Why this exists: rendering a date straight from an API field that is
// null/undefined, an empty string, or the wrong casing (e.g. `created_at`
// vs `createdAt`) produces `new Date(undefined)` → "Invalid Date" on screen.
// These helpers normalize that to an em-dash placeholder and never throw.
//
// BP 56 §7.1 — `locale` was already an optional param (defaulting to the
// browser); it now defaults to the active APP locale instead, so a French UI
// gets French date formatting even on an English-language OS/browser.
// Callers that pass an explicit `locale` are unaffected.

import { getActiveLocale, i18n } from '../i18n/runtime';

export type DateInput = string | number | Date | null | undefined;

const PLACEHOLDER = '—';

/** Coerce arbitrary input to a valid Date, or null if it can't be parsed. */
function toDate(value: DateInput): Date | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string' && value.trim() === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

/** Date only, e.g. "6/1/2026". Returns "—" for missing/invalid input. */
export function formatDate(
  value: DateInput,
  opts?: Intl.DateTimeFormatOptions,
  locale: string | string[] = getActiveLocale(),
): string {
  const d = toDate(value);
  return d ? d.toLocaleDateString(locale, opts) : PLACEHOLDER;
}

/** Date + time, e.g. "6/1/2026, 3:14:00 PM". Returns "—" for missing/invalid. */
export function formatDateTime(
  value: DateInput,
  opts?: Intl.DateTimeFormatOptions,
  locale: string | string[] = getActiveLocale(),
): string {
  const d = toDate(value);
  return d ? d.toLocaleString(locale, opts) : PLACEHOLDER;
}

/** Time only, e.g. "3:14:00 PM". Returns "—" for missing/invalid input. */
export function formatTime(
  value: DateInput,
  opts?: Intl.DateTimeFormatOptions,
  locale: string | string[] = getActiveLocale(),
): string {
  const d = toDate(value);
  return d ? d.toLocaleTimeString(locale, opts) : PLACEHOLDER;
}

// Audit-shaped surfaces (Users' access log, Audit Trail, Invitations) are read
// while correlating an incident against a bill or a support ticket, so a bare
// date is not enough — two events on the same day are indistinguishable in the
// row. Short form inline, full precision (with seconds) in a hover title. This
// pair was duplicated per-page (PlatformUsers defined it locally) until lifted
// here so every page that needs "when, precisely" renders it identically.

/** Short stamp for a table cell, e.g. "Jul 9, 3:14 PM". "—" if invalid. */
export function stampDateTime(value: DateInput): string {
  return formatDateTime(value, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/** Full precision for a hover title, e.g. "Thursday, July 9, 2026 at 3:14:00 PM". */
export function fullTimestamp(value: DateInput): string {
  return formatDateTime(value, { dateStyle: 'full', timeStyle: 'medium' });
}

/**
 * Compact relative phrase ("just now", "5m ago", "3h ago", "2d ago"),
 * falling back to a short absolute date beyond a week. "—" if invalid.
 */
export function relativeDate(value: DateInput, nowMs: number = Date.now()): string {
  const d = toDate(value);
  if (!d) return PLACEHOLDER;
  const diff = nowMs - d.getTime();
  if (diff < 0) return formatDate(d, { month: 'short', day: 'numeric' });
  const mins = Math.floor(diff / 60_000);
  // Non-hook `i18n.t()` — this is a plain utility, not a component, so it
  // reads the currently-active language directly off the shared i18next
  // instance rather than via `useTranslation()`.
  if (mins < 1) return i18n.t('common:relativeTime.justNow');
  if (mins < 60) return i18n.t('common:relativeTime.minutesAgo', { count: mins });
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return i18n.t('common:relativeTime.hoursAgo', { count: hrs });
  const days = Math.floor(hrs / 24);
  if (days < 7) return i18n.t('common:relativeTime.daysAgo', { count: days });
  return formatDate(d, { month: 'short', day: 'numeric' });
}
